<template>
    <section v-if="visible" class="landlord-journey-panel" aria-labelledby="landlord-journey-heading">
        <h2 id="landlord-journey-heading" class="landlord-journey-panel__sr-only">{{ headingText }}</h2>

        <div v-if="showEmpty"
             class="landlord-journey-panel__empty"
             role="status">
            <h3 class="landlord-journey-panel__empty-title">{{ label(emptyState.title_key) }}</h3>
            <p class="landlord-journey-panel__empty-body">{{ label(emptyState.body_key) }}</p>
            <a v-if="emptyState && emptyState.cta && emptyState.cta.url"
               class="landlord-journey-panel__empty-cta"
               :href="emptyState.cta.url">
                {{ label(emptyState.cta.label_key) }}
            </a>
        </div>

        <ul v-else class="landlord-journey-panel__cards">
            <li v-for="row in journey"
                :key="row.property_id"
                class="landlord-journey-panel__card"
                role="group"
                :aria-label="row.title">
                <header class="landlord-journey-panel__card-head">
                    <div class="landlord-journey-panel__card-meta">
                        <span class="landlord-journey-panel__badge"
                              :class="badgeModifier(row.badge_key)">{{ label(row.badge_key) }}</span>
                        <h3 class="landlord-journey-panel__card-title">{{ row.title }}</h3>
                    </div>
                    <template v-if="row.primary_cta && row.primary_cta.label_key">
                        <!-- Publication request is a state change: POST, never a GET link. -->
                        <button v-if="row.primary_cta.kind === 'request_publication' && row.primary_cta.url"
                                type="button"
                                class="landlord-journey-panel__primary-cta"
                                :class="primaryCtaModifier(row.primary_cta.kind)"
                                :disabled="requestingRowId === row.property_id"
                                @click="requestPublication(row)">
                            {{ label(row.primary_cta.label_key) }}
                        </button>
                        <!-- Already requested (status = concept_in_progress): passive, non-clickable. -->
                        <span v-else-if="row.primary_cta.kind === 'publication_requested'"
                              class="landlord-journey-panel__primary-cta"
                              :class="primaryCtaModifier(row.primary_cta.kind)"
                              role="status">
                            {{ label(row.primary_cta.label_key) }}
                        </span>
                        <a v-else-if="row.primary_cta.url"
                           class="landlord-journey-panel__primary-cta"
                           :class="primaryCtaModifier(row.primary_cta.kind)"
                           :href="row.primary_cta.url"
                           :target="row.primary_cta.kind === 'view_listing' ? '_blank' : null"
                           :rel="row.primary_cta.kind === 'view_listing' ? 'noopener noreferrer' : null">
                            {{ label(row.primary_cta.label_key) }}
                        </a>
                    </template>
                </header>

                <div class="landlord-journey-panel__progress" v-if="row.progress">
                    <span class="landlord-journey-panel__progress-text">
                        {{ row.progress.done }} {{ label('landlord.journey.progress.label') }}
                        {{ row.progress.total }} {{ label('landlord.journey.progress.items_complete') }}
                    </span>
                    <div class="landlord-journey-panel__progress-track"
                         :aria-valuenow="row.progress.done"
                         :aria-valuemin="0"
                         :aria-valuemax="row.progress.total"
                         role="progressbar">
                        <div class="landlord-journey-panel__progress-fill"
                             :style="{ width: progressPercent(row.progress) + '%' }"></div>
                    </div>
                </div>

                <ul class="landlord-journey-panel__checklist" v-if="row.items && row.items.length">
                    <li v-for="item in row.items"
                        :key="item.key"
                        class="landlord-journey-panel__checklist-item"
                        :class="{ 'is-done': item.done }">
                        <span class="landlord-journey-panel__checklist-icon"
                              :aria-hidden="true">{{ item.done ? '✓' : '·' }}</span>
                        <span class="landlord-journey-panel__checklist-label">{{ label(item.label_key) }}<span
                              v-if="!item.done && item.missing_sub"
                              class="landlord-journey-panel__checklist-sub-hint">{{ label('landlord.journey.specs_missing.' + item.missing_sub) }}</span></span>
                        <a v-if="!item.done && item.url"
                           class="landlord-journey-panel__checklist-action"
                           :href="item.url">
                            {{ label(item.action_key) }}
                        </a>
                        <span v-else-if="!item.done"
                              class="landlord-journey-panel__checklist-action landlord-journey-panel__checklist-action--muted">
                            {{ label(item.action_key) }}
                        </span>
                    </li>
                </ul>

                <div v-if="row.share && row.share.visible"
                     class="landlord-journey-panel__share"
                     :aria-label="label('landlord.journey.share.title')">
                    <span class="landlord-journey-panel__share-title">{{ label('landlord.journey.share.title') }}</span>
                    <div class="landlord-journey-panel__share-actions">
                        <button type="button"
                                class="landlord-journey-panel__share-btn landlord-journey-panel__share-btn--copy"
                                @click="copyShareLink(row)">
                            {{ copiedRowId === row.property_id
                                ? label('landlord.journey.share.copied')
                                : label('landlord.journey.share.copy') }}
                        </button>
                        <a class="landlord-journey-panel__share-btn landlord-journey-panel__share-btn--whatsapp"
                           :href="row.share.whatsapp_url"
                           target="_blank"
                           rel="noopener noreferrer">{{ label('landlord.journey.share.whatsapp') }}</a>
                        <a class="landlord-journey-panel__share-btn landlord-journey-panel__share-btn--linkedin"
                           :href="row.share.linkedin_url"
                           target="_blank"
                           rel="noopener noreferrer">{{ label('landlord.journey.share.linkedin') }}</a>
                        <a class="landlord-journey-panel__share-btn landlord-journey-panel__share-btn--email"
                           :href="row.share.mailto_url">{{ label('landlord.journey.share.email') }}</a>
                    </div>
                </div>
            </li>
        </ul>
    </section>
</template>

<script>
export default {
    name: 'LandlordJourneyPanel',
    props: {
        journey:     { type: Array,  default: function () { return []; } },
        emptyState:  { type: Object, default: null },
        i18n:        { type: Object, default: function () { return {}; } },
    },
    data: function () {
        return {
            copiedRowId: null,
            copiedTimer: null,
            requestingRowId: null,
        };
    },
    computed: {
        hasRows: function () {
            return Array.isArray(this.journey) && this.journey.length > 0;
        },
        showEmpty: function () {
            return !this.hasRows && this.emptyState && this.emptyState.title_key;
        },
        visible: function () {
            return this.hasRows || this.showEmpty;
        },
        headingText: function () {
            return this.label(this.hasRows ? 'landlord.journey.progress.steps' : 'landlord.journey.empty.title');
        },
    },
    methods: {
        label: function (key) {
            if (!key) return '';
            var value = this.i18n[key];
            return (typeof value === 'string' && value !== '') ? value : '';
        },
        badgeModifier: function (key) {
            switch (key) {
                case 'landlord.journey.badge.ready_to_publish':
                    return 'landlord-journey-panel__badge--ready';
                case 'landlord.journey.badge.live':
                    return 'landlord-journey-panel__badge--live';
                case 'landlord.journey.badge.live_no_viewings':
                    return 'landlord-journey-panel__badge--live';
                case 'landlord.journey.badge.almost_ready':
                    return 'landlord-journey-panel__badge--almost';
                default:
                    return 'landlord-journey-panel__badge--concept';
            }
        },
        primaryCtaModifier: function (kind) {
            switch (kind) {
                case 'go_live':
                case 'request_publication':
                    return 'landlord-journey-panel__primary-cta--go-live';
                case 'publication_requested':
                    return 'landlord-journey-panel__primary-cta--requested';
                case 'view_listing':
                    return 'landlord-journey-panel__primary-cta--view';
                case 'plan_viewings':
                    return 'landlord-journey-panel__primary-cta--plan';
                default:
                    return 'landlord-journey-panel__primary-cta--fill';
            }
        },
        requestPublication: function (row) {
            if (!row || !row.primary_cta || !row.primary_cta.url) return;
            if (this.requestingRowId) return;
            var vm = this;
            vm.requestingRowId = row.property_id;
            // Reuse the app's axios instance (X-CSRF-TOKEN + X-Requested-With are
            // set globally in bootstrap.js). On success the status is now
            // concept_in_progress, so a reload re-renders the passive state.
            if (window.axios && typeof window.axios.post === 'function') {
                window.axios.post(row.primary_cta.url)
                    .then(function () { window.location.reload(); })
                    .catch(function () { vm.requestingRowId = null; });
                return;
            }
            vm.requestingRowId = null;
        },
        progressPercent: function (progress) {
            if (!progress || !progress.total) return 0;
            var pct = Math.round((progress.done / progress.total) * 100);
            return Math.max(0, Math.min(100, pct));
        },
        copyShareLink: function (row) {
            if (!row || !row.share || !row.share.listing_url) return;
            var vm  = this;
            var url = row.share.listing_url;
            var done = function () {
                vm.copiedRowId = row.property_id;
                if (vm.copiedTimer) {
                    clearTimeout(vm.copiedTimer);
                }
                vm.copiedTimer = setTimeout(function () {
                    vm.copiedRowId = null;
                }, 1800);
            };
            try {
                if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                    navigator.clipboard.writeText(url).then(done, function () {
                        vm.copyFallback(url, done);
                    });
                    return;
                }
            } catch (_e) { /* fall through to manual */ }
            this.copyFallback(url, done);
        },
        copyFallback: function (text, done) {
            try {
                var ta = document.createElement('textarea');
                ta.value = text;
                ta.setAttribute('readonly', '');
                ta.style.position = 'absolute';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                done();
            } catch (_e) { /* silent */ }
        },
    },
};
</script>
